import React from 'react';
import { isArray, isObject, isFunction, get } from 'lodash';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { EDGE_DEMO_URL_FLAG } from '../constants/commonConstants';

export const checkFileExt = (filename, ext = 'js', isEqual = false) => (filename.toLowerCase().split('.').pop() === ext) === isEqual;

const paramToString = (key, value) => {
  let result = [];

  if (isArray(value)) {
    result = result.concat(value.map((item) => `${key}=${item}`));
  } else {
    result.push(`${key}=${value}`);
  }

  return result;
};

export const appendQueryParams = (path = '', params = {}) => {
  const updatedPath = path.includes('?') ? path : `${path}?`;
  const pairs = Object.keys(params).reduce((acc, key) => acc.concat(paramToString(key, params[key])), []);

  return pairs.length ? updatedPath + pairs.join('&') : path;
};

export const appendInlineParams = (path = '', params = {}) =>
  Object.keys(params).reduce((acc, key) => acc.replace(`:${key}`, params[key]), path);

export const withRouter = (Component) =>
  function Wrapper(props) {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();

    return <Component {...props} location={location} navigate={navigate} params={params} />;
  };

export const extractQueryParams = (url) => {
  const searchObject = {};
  const queries = url.split('?')[1].split('&');

  for (let i = 0; i < queries.length; i++) {
    const split = queries[i].split('=');

    searchObject[split[0]] = split[1];
  }

  return searchObject;
};

const makeChainSuccessResponseHandler =
  ({ resolve, collectedData, responseDataPath }) =>
  (response) => {
    resolve([...collectedData, ...get(response, responseDataPath, [])]);
  };

export const makeChainFailureResponseHandler =
  ({ reject, collectedData }) =>
  (err) => {
    reject({ err, collectedData });
  };

export const makeChainRequest = (props) => {
  const {
    request,
    per = 10,
    queryParams,
    bodyParams,
    successResponseHandlerMaker = makeChainSuccessResponseHandler,
    failureResponseHandlerMaker = makeChainFailureResponseHandler,
    responseDataPath = 'data.payloads',
    nameOfPerParam = 'page_size',
    nameOfPage = 'page_token',
    pathOfTotal = 'data.total_size',
    total = 1,
    isInterrupt = () => false,
  } = props;

  return request({ ...queryParams, [nameOfPerParam]: 1, [nameOfPage]: 0 }, bodyParams)
    .then((response) => {
      const chunkCount = Math.ceil((get(response, pathOfTotal) || total) / per);
      let promise = new Promise((resolve) => resolve([]));

      for (let i = 0; i < chunkCount; i++) {
        promise = promise.then((collectedData) => {
          if (isInterrupt()) return false;

          return new Promise((resolve, reject) => {
            request({ ...queryParams, [nameOfPerParam]: per, [nameOfPage]: i }, bodyParams)
              .then(
                successResponseHandlerMaker({
                  resolve,
                  collectedData,
                  responseDataPath,
                  pathOfTotal,
                }),
              )
              .catch(failureResponseHandlerMaker({ reject, collectedData }));
          });
        });
      }

      return promise;
    })
    .catch((err) => console.log(err));
};

export const defaultErrorMessages = {
  403: 'You do not have enough permissions',
  500: 'Unqualified error',
  default: 'Unqualified error',
};

export const DEFAULT_HANDLER_ERROR_MESSAGE = 'Handler is not a function';
export const CONFIG_ERROR_MESSAGE = 'The "config" param should be an object';

/**
 *
 * @param defaultHandler = (defaultMessage, error) => void
 * @param config = { [key: statusCode]: (defaultMessage, error) => void || string }
 * @return (error) => void
 */
export const makeErrorHandler =
  (defaultHandler, config = {}) =>
  (error) => {
    if (!isFunction(defaultHandler)) throw Error(DEFAULT_HANDLER_ERROR_MESSAGE);
    if (!isObject(config)) throw Error(CONFIG_ERROR_MESSAGE);

    const status = get(error, 'response.status', 'default');
    const defaultMessage = defaultErrorMessages[status] || defaultErrorMessages.default;
    const handler = config[status];

    if (isFunction(handler)) {
      handler(defaultMessage, error);
    } else if (handler) {
      defaultHandler(handler, error);
    } else {
      defaultHandler(defaultMessage, error);
    }
  };

export const setEnergyOutput = (value) => value && value.toFixed(3);

export const convertKWh = (value) => {
  const ABS_VALUE = Math.abs(value);

  if (ABS_VALUE >= 1_000_000) {
    return { value: setEnergyOutput(value / 1_000_000), unit: 'GWh' };
  }

  if (ABS_VALUE >= 1_000) {
    return { value: setEnergyOutput(value / 1_000), unit: 'MWh' };
  }

  return { value: setEnergyOutput(value), unit: 'kWh' };
};

export const stripQuery = (path = '') => path.split('?')[0];

export const isEdgeDemo = () => window.location.search.includes(EDGE_DEMO_URL_FLAG) || window.location.host.includes(EDGE_DEMO_URL_FLAG);
